import { type ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibSelect } from '@chewy/kib-fields-new-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import * as R from 'ramda'
import { DateFormat, DateUtils, RhapTable } from '@pbt/pbt-ui-components'
import type { RowData } from '@pbt/pbt-ui-components/src/components/tables/RhapTable'
import { convertWeight } from '@pbt/pbt-ui-components/src/utils/unitUtils'

import { VitalsNames } from '~/constants/vitals'
import { useNormalizedVitalsSubscription } from '~/hooks/useNormalizedVitalsSubscription'
import i18nPortal from '~/locales/i18n'
import { getUnitsState } from '~/store/duck/settings'
import type { VitalChartItem } from '~/types'

import RhapVitalsChart from './RhapVitalsChart'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-select.css'
import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './RhapVitalsWidget.module.scss'

const MAX_VITAL_DATA_POINTS = 6

const columns = [
  {
    header: i18nPortal.t('Common:DATE_TIME'),
    renderCell: (rowData: RowData) => rowData.date || '',
  },
  {
    header: i18nPortal.t('Common:VALUE'),
    renderCell: (rowData: RowData) => rowData.value || '',
  },
  {
    header: i18nPortal.t('Common:NOTES'),
    renderCell: (rowData: RowData) => rowData.notes || '',
  },
]

const RhapVitalsWidget = ({ patientId }: { patientId: string }) => {
  const { t } = useTranslation(['Soap'])

  const [activeVital, setActiveVital] = useState<
    (typeof VitalsNames)[keyof typeof VitalsNames]
  >(VitalsNames.weight)

  const unitsState = useSelector(getUnitsState)
  const [currentWeightUnit, setCurrentWeightUnit] = useState(unitsState.weight)
  const vitalsSelectRef = useRef<HTMLSelectElement>(null)

  const chartVitals = [VitalsNames.weight, VitalsNames.temperature]
  const isVitalWeight = activeVital === VitalsNames.weight
  const isVitalWeightOrTemperature = chartVitals.includes(activeVital)

  const vitalsList = R.keys(VitalsNames).map((id) => ({
    id,
    name: VitalsNames[id],
  }))

  // Use custom hook for normalized vitals data
  const { vitals, loading: isVitalsLoading } =
    useNormalizedVitalsSubscription(patientId)

  // Find the key that matches the active vital value
  const activeVitalKey = Object.keys(VitalsNames).find(
    (key) => VitalsNames[key as keyof typeof VitalsNames] === activeVital,
  ) as keyof typeof VitalsNames

  const activeVitalData = vitals?.[activeVitalKey] || []

  // Chart vital data for chart
  const chartData: VitalChartItem[] = activeVitalData
    .map((vital) => ({
      displayValue: isVitalWeight
        ? `${String(convertWeight(Number(vital.value), currentWeightUnit))} ${currentWeightUnit}`
        : `${String(vital.value)}`,
      label: DateUtils.formatDate(
        vital.creationDate,
        undefined,
        DateFormat.FULL_DATE_YEAR_ABBREV,
      ),
      value: Number(vital.value), // Keep original value for consistent chart positioning
    }))
    .splice(0, MAX_VITAL_DATA_POINTS) // We only want the last 6 vital data points
    .reverse()

  // Non-chart vital data for table
  const rows = activeVitalData.map((vital) => ({
    date: DateUtils.formatDate(
      vital.creationDate,
      undefined,
      DateFormat.FULL_DATE_YEAR_ABBREV,
    ),
    value: isVitalWeight
      ? `${convertWeight(Number(vital.value), currentWeightUnit)} ${currentWeightUnit}`
      : vital.value,
    notes: vital.notes,
  }))

  const handleVitalChange = (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
  ) => {
    const newValue = event.target
      .value as (typeof VitalsNames)[keyof typeof VitalsNames]
    setActiveVital(newValue)
  }

  const handleWeightUnitChange = (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
  ) => {
    const newWeightUnit = event.target.value
    setCurrentWeightUnit(newWeightUnit)
  }

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.titleContainer}>
        <h2 className={styles.title}>Vitals</h2>
      </div>
      <div className={styles.header}>
        <KibSelect
          hiddenLabel
          className={styles.vitalsSelect}
          label={t('Soap:VITALS_WIDGET.VITAL_SELECT.LABEL')}
          ref={vitalsSelectRef}
          value={activeVital}
          onChange={handleVitalChange}
        >
          {vitalsList.map((vital) => (
            <option key={vital.id} value={vital.name}>
              {vital.name}
            </option>
          ))}
        </KibSelect>
        {isVitalWeight && (
          <KibSelect
            hiddenLabel
            className={styles.weightUnitSelect}
            label={t('Soap:VITALS_WIDGET.WEIGHT_UNIT_SELECT.LABEL')}
            value={currentWeightUnit}
            onChange={handleWeightUnitChange}
          >
            <option value="lb">
              {t('Soap:VITALS_WIDGET.WEIGHT_UNIT_SELECT.OPTION.POUNDS')}
            </option>
            <option value="kg">
              {t('Soap:VITALS_WIDGET.WEIGHT_UNIT_SELECT.OPTION.KILOGRAMS')}
            </option>
          </KibSelect>
        )}
      </div>
      {isVitalsLoading ? (
        <div
          className={styles.skeletonsContainer}
          data-testid="Skeletons container"
        >
          <KibSkeletonText lines={3} short={false} />
        </div>
      ) : chartData.length && isVitalWeightOrTemperature ? (
        <RhapVitalsChart data={chartData} />
      ) : rows.length ? (
        <RhapTable columns={columns} rows={rows} />
      ) : (
        <div className={styles.emptyStateContainer}>
          <span>{t('Soap:VITALS_WIDGET.NO_RECORDED_VITALS')}</span>
        </div>
      )}
    </div>
  )
}

export default RhapVitalsWidget
